import { Loading01Icon } from '@langchain/untitled-ui-icons';

import { forwardRef } from 'react';

import { cn } from '../../utils/cn';
import { buttonStyleMap } from '../Button/constants';
import { Tooltip, TooltipProps } from '../Tooltip/';

// Type to constrain icons to only accept icons from @langchain/untitled-ui-icons
export type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Button color scheme */
  color?: 'primary' | 'secondary' | 'error';
  /** Button visual variant */
  variant?: 'normal' | 'outlined' | 'plain';
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the button should have rounded-full styling */
  round?: boolean;
  /** Icon component from @langchain/untitled-ui-icons to display */
  icon: IconComponent;
  /** Label for the button tooltip and aria-label */
  label: string;
  /** Class name for the icon */
  iconClassName?: string;
  /** Size of the button */
  size?: 'sm' | 'md';
  /** Whether the button is loading */
  loading?: boolean;
  /** Tooltip props */
  tooltipProps?: Omit<TooltipProps, 'children'>;
}

const Spinner = () => {
  return <Loading01Icon className="size-4 animate-spin" />;
};

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      color = 'primary',
      variant = 'normal',
      onClick,
      disabled = false,
      round = false,
      size = 'sm',
      icon: Icon,
      label,
      className,
      iconClassName,
      tooltipProps,
      loading = false,
      ...props
    },
    ref
  ) => {
    const baseStyles = cn(
      'inline-flex items-center justify-center transition-all duration-200',
      {
        'p-1': size === 'sm',
        'p-2': size === 'md',
      }, // Square padding for icon buttons
      {
        'shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]':
          (variant === 'normal' || variant === 'outlined') && !disabled,
        // Apply rounded-full if round prop is true, otherwise use rounded-md
        'rounded-full': round,
        'rounded-sm': !round && size === 'sm',
        'rounded-md': !round && size === 'md',
      }
    );

    // Generate unique class combinations for each color/variant combination
    // This logic is extracted from the Button component to maintain consistency
    const getColorVariantStyles = () => {
      // Define styles for each specific combination

      // Generate the key for the current combination
      const state = disabled ? 'disabled' : 'enabled';
      const key = `${color}-${variant}-${state}` as keyof typeof buttonStyleMap;

      return buttonStyleMap[key] || '';
    };

    return (
      <Tooltip
        {...tooltipProps}
        title={tooltipProps?.title ?? label}
        side={tooltipProps?.side ?? 'top'}
      >
        <button
          ref={ref}
          type="button"
          className={cn(baseStyles, getColorVariantStyles(), className)}
          disabled={disabled}
          onClick={onClick}
          aria-label={label}
          {...props}
        >
          {loading ? (
            <Spinner />
          ) : (
            <Icon
              className={cn(
                'h-4 w-4 flex-shrink-0',
                '[&_path]:stroke-[1.5px]',
                disabled && 'opacity-50',
                iconClassName
              )}
            />
          )}
        </button>
      </Tooltip>
    );
  }
);

IconButton.displayName = 'IconButton';

export { IconButton };
export type { IconButtonProps };
